import { useState } from 'react'
import { LONG_OVERDUE_DAYS, daysOverdue, sortProjectActions, todayIso } from '../../../shared/projects.js'
import { Dialog } from '../ui/Dialog.jsx'
import { fmtDate } from './fields.jsx'
import styles from './projects.module.css'

/** The flag on an action item: Long overdue (red), Overdue (amber), due date, or Done. */
function dueFlag(action, today) {
  if (action.done) return ['Done', styles.pNone]
  const days = daysOverdue(action.dueOn, today)
  if (days > LONG_OVERDUE_DAYS) return [`Long overdue · ${days} days`, styles.pBad]
  if (days > 0) return [`Overdue · ${days} day${days > 1 ? 's' : ''}`, styles.pReq]
  return action.dueOn ? [`Due ${fmtDate(action.dueOn)}`, styles.pNone] : ['No due date', styles.pNone]
}

/** One card: the project's action items, with "Add action item" opening a dialog. */
export function ProjectActions({ project, api, canEdit }) {
  const [adding, setAdding] = useState(false)
  const today = todayIso()
  const actions = sortProjectActions(project.actions, today)
  const open = actions.filter((a) => !a.done).length
  const longOverdue = actions.filter((a) => !a.done && daysOverdue(a.dueOn, today) > LONG_OVERDUE_DAYS).length

  return (
    <section className={styles.panel}>
      <div className={`${styles.row} ${styles.between}`} style={{ marginBottom: 8 }}>
        <h2 className={styles.h} style={{ margin: 0 }}>
          Action items
          <span className={styles.muted} style={{ textTransform: 'none', letterSpacing: 0, fontFamily: 'inherit', fontSize: 13 }}>
            {open} open{longOverdue ? ` · ${longOverdue} long overdue` : ''}
          </span>
        </h2>
        {canEdit ? (
          <button type="button" className={styles.primary} onClick={() => setAdding(true)}>
            Add action item
          </button>
        ) : null}
      </div>

      {actions.length === 0 ? <p className={styles.muted}>No action items yet. Add one, or move one from a Sybill meeting.</p> : null}
      {actions.map((a) => {
        const [label, tone] = dueFlag(a, today)
        const long = !a.done && daysOverdue(a.dueOn, today) > LONG_OVERDUE_DAYS
        return (
          <div key={a.id} className={`${styles.action} ${a.done ? styles.actionDone : ''} ${long ? styles.actionLong : ''}`}>
            <input type="checkbox" checked={a.done} disabled={!canEdit} aria-label={`Mark “${a.subject}” done`} onChange={(e) => api.patchAction(project.id, a.id, { done: e.target.checked })} />
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div className={styles.actionSubject}>{a.subject}</div>
              {a.description ? <div className={styles.muted}>{a.description}</div> : null}
              <div className={styles.muted} style={{ fontSize: 12 }}>
                {a.source === 'sybill' ? 'From a Sybill meeting' : 'Added manually'}
              </div>
            </div>
            <span className={`${styles.pill} ${tone}`}>{label}</span>
            {canEdit ? (
              <button type="button" className={`${styles.ghost} ${styles.dangerGhost}`} aria-label={`Delete “${a.subject}”`} onClick={() => api.removeAction(project.id, a.id)}>
                Delete
              </button>
            ) : null}
          </div>
        )
      })}

      <AddActionDialog open={adding} onClose={() => setAdding(false)} onCreate={(body) => api.addAction(project.id, body)} />
    </section>
  )
}

function AddActionDialog({ open, onClose, onCreate }) {
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [dueOn, setDueOn] = useState('')
  const [busy, setBusy] = useState(false)

  const close = () => {
    setSubject('')
    setDescription('')
    setDueOn('')
    onClose()
  }
  const submit = async (e) => {
    e.preventDefault()
    if (subject.trim().length < 2) return
    setBusy(true)
    const created = await onCreate({ subject: subject.trim(), description: description.trim(), dueOn: dueOn || null })
    setBusy(false)
    if (created) close()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="New action item"
      footer={
        <>
          <button type="button" className={styles.ghost} onClick={close}>
            Cancel
          </button>
          <button type="submit" form="new-action" className={styles.primary} disabled={subject.trim().length < 2 || busy}>
            Create action item
          </button>
        </>
      }
    >
      <form id="new-action" className={styles.formOne} onSubmit={submit}>
        <label className={styles.fieldLabel}>
          Subject
          <input className={styles.input} value={subject} maxLength={120} autoFocus placeholder="e.g. Confirm go-live date with the client" onChange={(e) => setSubject(e.target.value)} />
        </label>
        <label className={styles.fieldLabel}>
          Description
          <textarea className={styles.textarea} value={description} maxLength={1000} placeholder="What needs to happen, and any detail the next person needs." onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className={styles.fieldLabel}>
          Due date (optional)
          <input className={styles.input} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        </label>
      </form>
    </Dialog>
  )
}
